import { Flex } from '@radix-ui/themes'
import { DataTestIDs } from 'shared/constants'
import { FlashIcon, WalletIcon } from 'shared/icons'
import { NeonPanel } from 'shared/ui/neon'
import { shortenAddress } from 'shared/utils/shorten-address'
import type { WalletBalance } from '../types'
import { CopyAddressButton } from './copy-address-button'
import { WalletBalances } from './wallet-balances'
import { WalletCardActions } from './wallet-card-actions'
import type { WalletConnectProps } from './wallet-card-actions'
import { WalletManageActions } from './wallet-manage-actions'
import type { WalletManageProps } from './wallet-manage-actions'
import styles from '../neon-wallets.module.scss'

export type { WalletConnectProps } from './wallet-card-actions'

interface Props {
  address: string
  isPrimary: boolean
  /** Connected in this browser, so the wallet that plays. */
  isPlaying: boolean
  /** The embedded wallet Privy created at sign-up. */
  isEmbedded?: boolean
  balance?: WalletBalance
  onDeposit: (address: string) => void
  /** Offered while the wallet is not the one that plays. */
  connect?: WalletConnectProps
  /** Make primary / unlink (FE-305); without it the card offers neither. */
  manage?: WalletManageProps
}

type HeaderProps = Required<
  Pick<Props, 'address' | 'isPrimary' | 'isPlaying' | 'isEmbedded'>
>

const WalletCardHeader: React.FC<HeaderProps> = ({
  address,
  isPrimary,
  isPlaying,
  isEmbedded
}) => (
  <Flex
    align='center'
    gap='3'
  >
    <span
      className={styles.walletAvatar}
      data-playing={isPlaying}
    >
      <WalletIcon />
    </span>

    <Flex
      direction='column'
      minWidth='0'
      flexGrow='1'
    >
      <Flex
        align='center'
        gap='2'
        wrap='wrap'
      >
        <span className={styles.walletName}>
          {isEmbedded ? 'XYRO wallet' : 'External wallet'}
        </span>
        {isPrimary ?
          <span
            className={styles.chip}
            data-tone='gold'
          >
            Primary
          </span>
        : null}
      </Flex>
      <Flex
        align='center'
        gap='1'
      >
        <span
          className={styles.walletAddress}
          title={address}
        >
          {shortenAddress(address)}
        </span>
        <CopyAddressButton
          address={address}
          testId={DataTestIDs.buttonPrivyWalletCopy}
        />
      </Flex>
    </Flex>

    {isPlaying ?
      <span className={styles.playingChip}>
        <FlashIcon />
        Playing
      </span>
    : null}
  </Flex>
)

/**
 * One linked wallet: what it holds, whether it plays (it is the connected
 * one), how to connect it, how to top it up and, with `manage`, how to make
 * it primary or unlink it. The primary wallet is never unlinked from here
 * (another one has to become primary first), nor the XYRO wallet Privy
 * created: Privy keeps it, so it would never come back.
 */
export const WalletListItem: React.FC<Props> = ({
  address,
  isPrimary,
  isPlaying,
  isEmbedded = false,
  balance,
  onDeposit,
  connect,
  manage
}) => (
  <NeonPanel
    glow={isPlaying ? 'green' : 'none'}
    padding='1'
    gap='3'
    data-playing={isPlaying}
    data-testid={DataTestIDs.walletsSettingsRow}
  >
    <WalletCardHeader
      address={address}
      isPrimary={isPrimary}
      isPlaying={isPlaying}
      isEmbedded={isEmbedded}
    />

    <WalletBalances balance={balance} />

    <WalletCardActions
      address={address}
      onDeposit={onDeposit}
      connect={isPlaying ? undefined : connect}
    />

    {manage ?
      <WalletManageActions
        address={address}
        canMakePrimary={!isPrimary}
        canUnlink={!isPrimary && !isEmbedded}
        {...manage}
      />
    : null}
  </NeonPanel>
)
